import React, { useState, ChangeEvent, useRef, useEffect, CSSProperties, useCallback } from 'react';

import Input, { TextArea } from '@vromo/ui/components/Input';
import Button from '@vromo/ui/components/Button';
import Select from '@vromo/ui/components/Select';
import Modal from '@vromo/ui/components/Modal';
import RadioGroup from '@vromo/ui/components/Radio';
import ExpandingDrawer from '@vromo/ui/components/ExpandingDrawer';
import ReactSelect from 'react-select';
import { useStore } from '@vromo/mobx-react';
import { observer } from 'mobx-react-lite';
import { action, toJS } from 'mobx';

import { LocalStorage } from '@vromo/models';

import { parsePhoneNumberFromString } from 'libphonenumber-js';

import ControlledTelInput from './common/components/ControlledTelInput/ControlledTelInput';
import { observable } from 'mobx';

import FontAwesome from '@vromo/ui/components/FontAwesome';
import { RootStore } from '@vromo/dashboard/src/stores/RootStore';
import { update } from 'serializr';
import { Group } from '@vromo/models';
import { Contact } from '@vromo/models';
import { EntityList } from '@vromo/models';
import { getCode, getNames } from 'country-list';
import { useTranslation } from 'react-i18next';
import { zapierSignupSurveyUrl } from '@vromo/api/constants';

const styles: { [key: string]: CSSProperties } = {
    mainTitle: {
        marginTop: 0,
        marginBottom: 28
    },
    label: {
        marginTop: 6
    },
    buttonContainer: {
        textAlign: 'right',
        marginTop: 16
    },
    phoneInputContainer: {
        position: 'relative'
    },
    phoneNumberWarning: {
        position: 'absolute',
        right: 8,
        top: '50%',
        transform: 'translate(0, -50%)',
        color: '#ffbe46'
    },
    radioGroup: {
        marginTop: 4
    },
    cityInput: {
        height: '64px'
    }
};

type Country = {
    alpha2Code: string;
    name: string;
};

type SignupSurveyData = {
    name: string;
    email: string;
    phone: string;
    country: string;

    company: string;
    jobTitle: string;
    businessType: string;
    employees: string;
    orders: string;
    goals: string;
    group: string;
    driverType: string;
    needHelpFindingDrivers: string;
    cityCoverage: string;
};

export default observer(() => {
    const { t } = useTranslation();
    const {
        viewStore,
        groupStore,
        authStore,
        authStore: { role, graph }
    } = useStore<RootStore>('rootStore');

    const [country, setCountry] = useState<Country | null>(null);
    const [isOpen, setIsOpen] = useState(LocalStorage.isInitialSignup === true);

    const [page, setPage] = useState<number>(0);
    const [showAdditionalDriverQuestions, setShowAdditionalDriverQuestions] = useState<boolean>(
        false
    );

    const surveyData = useRef<SignupSurveyData>(
        observable({
            name: '',
            email: authStore.user?.contact?.email || '',
            phone: `+${viewStore.callingCode}`,
            country: '',
            company: '',
            jobTitle: '',
            businessType: t('signupSurvey.pageTwo.restaurantChain'),
            employees: '',
            orders: '',
            goals: t('signupSurvey.pageThree.marketingTool'),
            group: '0',
            driverType: 'my_drivers',
            needHelpFindingDrivers: 'no',
            cityCoverage: ''
        })
    ).current;

    const countries: Country[] = getNames()
        .sort()
        .map((name) => ({
            alpha2Code: getCode(name) || '',
            name
        }));

    const validate = useCallback(() => {
        if (page === 0) {
            return (
                surveyData.name &&
                parsePhoneNumberFromString(surveyData.phone)?.isValid() &&
                surveyData.email
            );
        } else if (page === 1) {
            return (
                surveyData.company &&
                surveyData.jobTitle &&
                surveyData.businessType &&
                surveyData.employees &&
                surveyData.orders
            );
        } else if (page === 2) {
            return surveyData.goals;
        }

        return true;
    }, [page, surveyData]);

    const updateField = (field: keyof typeof surveyData) => {
        return action((e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            surveyData[field] = e.target.value;
        });
    };

    const submitToZapier = (
        name: string,
        email: string,
        subject: string,
        surveyData: SignupSurveyData
    ) => {
        return fetch(zapierSignupSurveyUrl, {
            method: 'POST',
            body: JSON.stringify({
                requester: { name: `${name} - ${email}` },
                subject: subject,
                body: JSON.stringify({...surveyData, ...LocalStorage.utmVars})
            })
        });
    };

    useEffect(() => setShowAdditionalDriverQuestions(surveyData.driverType !== 'my_drivers'), [
        surveyData.driverType
    ]);

    const submit = async () => {
        try {
            await graph.root
                .graph()
                .user()
                .contact()
                .post(new Contact({ name: surveyData.name, phone: surveyData.phone }));

            const groups = new EntityList(Group);
            groups.push(
                new Group({
                    id: groupStore.groups.items[0]!.id,
                    name: surveyData.company
                })
            );

            // Rename parent level group to company name
            graph.root
                .graph()
                .roles(role.id)
                .groups()
                .post(groups)
                .then(({ items }) => {
                    const updatedGroup: Group = items[0]!;

                    action(() => {
                        authStore.role.group!.name = updatedGroup.name || '';

                        update(groupStore.groups, { items, type: 'update' });
                    });

                    submitToZapier(surveyData.name, surveyData.email, 'New sign up', {
                        ...toJS(surveyData),
                        country: country?.name || '',
                        group: updatedGroup.id!.toString() || '0'
                    });
                });

            setIsOpen(false);
            LocalStorage.isInitialSignup = false;
        } catch (e) {
            console.error(e);
        }
    };

    if (/@test$/.test(authStore.user?.contact?.email || '')) return null;

    return (
        <Modal isOpen={isOpen} allowEvents>
            <h2 style={styles.mainTitle}>{t('signupSurvey.surveyTitle')}</h2>
            {page === 0 ? (
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        setPage(1);
                    }}>
                    <h3>{t('signupSurvey.pageOne.title')}</h3>

                    <label style={styles.label}>{t('signupSurvey.pageOne.fullName')}</label>
                    <Input value={surveyData.name} onChange={updateField('name')} required />

                    <label style={styles.label}>{t('signupSurvey.pageOne.email')}</label>
                    <Input
                        type="email"
                        value={surveyData.email}
                        onChange={updateField('email')}
                        required
                    />

                    <label style={styles.label}>{t('signupSurvey.pageOne.phoneNumber')}</label>
                    <div style={styles.phoneInputContainer}>
                        <ControlledTelInput
                            value={surveyData.phone}
                            default={viewStore.callingCode}
                            defaultCountry={viewStore.country}
                            onChange={action((phone: string) => (surveyData.phone = phone))}
                        />
                        {!parsePhoneNumberFromString(surveyData.phone)?.isValid() ? (
                            <div style={styles.phoneNumberWarning}>
                                <FontAwesome icon="exclamation-triangle" />
                            </div>
                        ) : null}
                    </div>

                    <label style={styles.label}>{t('signupSurvey.pageOne.country')}</label>
                    <ReactSelect
                        value={country}
                        options={countries}
                        getOptionLabel={(it) => it.name}
                        getOptionValue={(it) => it.alpha2Code}
                        onChange={(it: Country | null) => setCountry(it)}
                    />

                    <div style={styles.buttonContainer}>
                        <Button type="submit" disabled={!validate()}>
                            {t('signupSurvey.next')}
                        </Button>
                    </div>
                </form>
            ) : null}

            {page === 1 ? (
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        setPage(2);
                    }}>
                    <h3>{t('signupSurvey.pageTwo.title')}</h3>

                    <label>{t('signupSurvey.pageTwo.companyName')}</label>
                    <Input value={surveyData.company} onChange={updateField('company')} />

                    <label style={styles.label}>{t('signupSurvey.pageTwo.jobTitle')}</label>
                    <Input value={surveyData.jobTitle} onChange={updateField('jobTitle')} />

                    <label style={styles.label}>{t('signupSurvey.pageTwo.businessType')}</label>
                    <Select onChange={updateField('businessType')}>
                        <option>{t('signupSurvey.pageTwo.restaurantChain')}</option>
                        <option>{t('signupSurvey.pageTwo.RDS')}</option>
                        <option>{t('signupSurvey.pageTwo.partyLogistics')}</option>
                        <option>{t('signupSurvey.pageTwo.retailBusiness')}</option>
                        <option>{t('signupSurvey.pageTwo.retailBrand')}</option>
                        <option>{t('signupSurvey.pageTwo.other')}</option>
                    </Select>

                    <label style={styles.label}>
                        {t('signupSurvey.pageTwo.numberOfEmployees')}
                    </label>
                    <Input value={surveyData.employees} onChange={updateField('employees')} />

                    <label style={styles.label}>{t('signupSurvey.pageTwo.ordersPerWeek')}</label>
                    <Input value={surveyData.orders} onChange={updateField('orders')} />

                    <label style={styles.label}>{t('signupSurvey.pageTwo.whoDelivers')}</label>
                    <div style={styles.radioGroup}>
                        <RadioGroup
                            name={'driverType'}
                            options={[
                                {
                                    label: t('signupSurvey.pageTwo.myDrivers'),
                                    value: 'my_drivers'
                                },
                                {
                                    label: t('signupSurvey.pageTwo.thirdPartyFleet'),
                                    value: '3rd_party_fleet'
                                },
                                {
                                    label: t('signupSurvey.pageTwo.bothDriverTypes'),
                                    value: 'combination'
                                },
                                {
                                    label: t('signupSurvey.pageTwo.noDelivery'),
                                    value: 'no_delivery'
                                }
                            ]}
                            direction="row"
                            onChange={updateField('driverType')}
                            checked={surveyData.driverType}
                        />
                    </div>
                    <ExpandingDrawer isOpen={showAdditionalDriverQuestions}>
                        <div>
                            <label style={styles.label}>
                                {t('signupSurvey.pageTwo.needHelpFindingDrivers')}
                            </label>
                            <div style={styles.radioGroup}>
                                <RadioGroup
                                    name={'needHelpFindingDrivers'}
                                    options={[
                                        {
                                            label: t('signupSurvey.pageTwo.yes'),
                                            value: 'yes'
                                        },
                                        {
                                            label: t('signupSurvey.pageTwo.no'),
                                            value: 'no'
                                        }
                                    ]}
                                    direction="row"
                                    onChange={updateField('needHelpFindingDrivers')}
                                    checked={surveyData.needHelpFindingDrivers}
                                />
                            </div>
                            <label style={styles.label}>
                                {t('signupSurvey.pageTwo.driverCoverage')}
                            </label>
                            <TextArea
                                placeholder={t('signupSurvey.pageTwo.cityInputPlaceholder')}
                                value={surveyData.cityCoverage}
                                onChange={updateField('cityCoverage')}
                                lockSize={true}
                                style={styles.cityInput}
                            />
                        </div>
                    </ExpandingDrawer>
                    <div style={styles.buttonContainer}>
                        <Button type="submit" disabled={!validate()}>
                            {t('signupSurvey.next')}
                        </Button>
                    </div>
                </form>
            ) : null}
            {page === 2 ? (
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        submit();
                    }}>
                    <h3>{t('signupSurvey.pageThree.title')}</h3>
                    <Select onChange={updateField('goals')}>
                        <option>{t('signupSurvey.pageThree.marketingTool')}</option>
                        <option>{t('signupSurvey.pageThree.improveEfficiency')}</option>
                        <option>{t('signupSurvey.pageThree.manageTeam')}</option>
                        <option>{t('signupSurvey.pageThree.moreVolume')}</option>
                        <option>{t('signupSurvey.pageThree.findAlternative')}</option>
                    </Select>
                    <div style={styles.buttonContainer}>
                        <Button type="submit">{t('signupSurvey.pageThree.finish')}</Button>
                    </div>
                </form>
            ) : null}
        </Modal>
    );
});
